// src/pages/SelectionOrders/components/edit/SelectionOrderHeaderForm.tsx
import React, { useEffect, useState } from 'react';
import SelectTwo from '../../../../components/SelectTwo';
import ButtonCancelForm from '../../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import { SelectionOrder } from '../../../../app/models/SelectionOrder';

type SelectOpt = { id: number | string; name: string };

interface Props {
  fetching?: boolean;
  order: SelectionOrder;
  setOrder: (o: SelectionOrder) => void;
  societies: SelectOpt[];
  units: SelectOpt[];
  statuses: SelectOpt[];
  errorFields?: any;
  onSave: (o: SelectionOrder) => void;
  onCancel: () => void;
  readOnly?: boolean;
  hideActions?: boolean;
}

const SelectionOrderHeaderForm = ({
  fetching,
  order,
  setOrder,
  societies,
  units,
  statuses,
  errorFields,
  onSave,
  onCancel,
  readOnly = false,
  hideActions = false,
}: Props) => {
  const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();
  useEffect(() => { setErrors(errorFields); }, [errorFields]);

  const [showStatusConfirmModal, setShowStatusConfirmModal] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<{ value: string; label: string } | null>(null);
  const [startDateForInProgress, setStartDateForInProgress] = useState('');
  const [endDateForFinalize, setEndDateForFinalize] = useState('');

  const mapOptions = (opts: SelectOpt[]) => opts.map(o => ({ value: o.id, label: o.name }));

  const buildDefaultDateTimeLocal = (raw?: string | null) => {
    if (raw && raw.trim()) {
      const s = String(raw);
      if (s.length >= 16) {
        return s.slice(0, 16);
      }
    }
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const y = now.getFullYear();
    const m = pad(now.getMonth() + 1);
    const d = pad(now.getDate());
    const hh = pad(now.getHours());
    const mm = pad(now.getMinutes());
    return `${y}-${m}-${d}T${hh}:${mm}`;
  };

  // Construir options del estado incluyendo el actual como "no seleccionable"
  const statusSelectableOpts = mapOptions(statuses); // vienen de la API: solo next_status
  const statusCurrentOpt =
    order.status
      ? [{ value: order.status, label: (order as any).status_label ?? order.status, __disabled: true }]
      : [];

  const statusOptions = [
    ...statusCurrentOpt,
    ...statusSelectableOpts.filter(o => !statusCurrentOpt.some(c => c.value === o.value)),
  ];

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setOrder({ ...order, [e.target.name]: e.target.value });
  };

  return (
    <>
      {/* Fila 1: Sociedad, Fecha de Programa */}
      <div className="row">
        <div className="col-md-6">
          <label className="col-form-label">Sociedad</label>
          {readOnly ? (
            <input className="form-control" value={(order as any).society ?? ''} disabled />
          ) : (
            <>
              <SelectTwo
                name="society_id"
                id="society_id"
                inputValue={order.society_id ?? null}
                options={mapOptions(societies)}
                hasError={fieldHasError('society_id') !== ''}
                onChange={(v: any) => setOrder({ ...order, society_id: v?.value ?? null })}
                onFocus={() => onFocusRemove('society_id')}
                placeholder="Seleccione sociedad"
                disabled={order.status !== 'CREATED'}
              />
              <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('society_id')}</div>
            </>
          )}
        </div>

        <div className="col-md-6">
          <label className="col-form-label">Fecha de programa</label>
          <input
            name="program_date"
            id="program_date"
            type="date"
            className={`form-control ${fieldHasError('program_date')}`}
            value={order.program_date ?? ''}
            onChange={onChange}
            onFocus={() => onFocusRemove('program_date')}
            disabled={readOnly || order.status !== 'CREATED'}
            title={order.status !== 'CREATED' ? 'Se bloquea al pasar a En progreso' : undefined}
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('program_date')}</div>
        </div>
      </div>

      {/* Fila 2: Fecha Inicio, Estado, Fecha Fin */}
      <div className="row mt-3">
        <div className="col-md-4">
          <label className="col-form-label">Fecha inicio</label>
          <input
            name="start_date"
            id="start_date"
            type="text"
            className={`form-control ${fieldHasError('start_date')}`}
            value={
              order.start_date
                ? String(order.start_date).replace('T', ' ').slice(0, 19)
                : ''
            }
            readOnly
            disabled
            title="Se registra al cambiar a En progreso"
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('start_date')}</div>
        </div>

        <div className="col-md-4" style={{ minWidth: 0 }}>
          <label className="col-form-label">Estado</label>
          {readOnly ? (
            <input className="form-control" value={order.status_label ?? order.status ?? ''} disabled />
          ) : (
            <>
              <SelectTwo
                name="status"
                id="estado"
                inputValue={order.status ?? null}
                options={statusOptions}
                hasError={fieldHasError('status') !== ''}
                onChange={(v: any) => {
                  if (!v?.value) return;
                  const value = String(v.value);
                  if (value === order.status) return;
                  setPendingStatus({ value, label: v.label });
                  if (value === 'IN_PROGRESS') {
                    setStartDateForInProgress(buildDefaultDateTimeLocal(order.start_date as any));
                  } else {
                    setStartDateForInProgress('');
                  }
                  if (value === 'FINALIZED') {
                    setEndDateForFinalize(buildDefaultDateTimeLocal(order.end_date as any));
                  } else {
                    setEndDateForFinalize('');
                  }
                  setShowStatusConfirmModal(true);
                }}
                onFocus={() => onFocusRemove('status')}
                placeholder="Seleccione estado"
              />
              <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('status')}</div>
            </>
          )}
        </div>

        <div className="col-md-4">
          <label className="col-form-label">Fecha fin</label>
          <input
            name="end_date"
            id="end_date"
            type="text"
            className={`form-control ${fieldHasError('end_date')}`}
            value={
              order.end_date
                ? String(order.end_date).replace('T', ' ').slice(0, 19)
                : ''
            }
            readOnly
            disabled
            title="Solo se asigna al finalizar la orden"
          />
          <div className="invalid-feedback" style={{ display: 'flex' }}>{fieldErrorMessage('end_date')}</div>
        </div>
      </div>

      {!hideActions && (
        <div className="row justify-content-end mt-3">
          <div className="col-auto">
            <ButtonCancelForm callbackCancel={onCancel} locked={fetching} />
          </div>
          <div className="col-auto">
            <ButtonSaveForm callbackSave={() => onSave(order)} locked={fetching} />
          </div>
        </div>
      )}

      {/* Modal de confirmación de cambio de estado */}
      {showStatusConfirmModal && pendingStatus && (
        <>
        <div className="modal-backdrop fade show" />
        <div className="modal fade show" style={{ display: 'block', zIndex: 1055 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar cambio de estado</h5>
                <button type="button" className="btn-close" onClick={() => {
                  setShowStatusConfirmModal(false);
                  setPendingStatus(null);
                  setStartDateForInProgress('');
                  setEndDateForFinalize('');
                }} />
              </div>
              <div className="modal-body">
                <p>
                  ¿Desea confirmar el cambio de estado a <strong>{pendingStatus.label}</strong>?
                </p>
                {pendingStatus.value === 'IN_PROGRESS' && (
                  <div className="mb-3">
                    <label className="form-label">Fecha inicio</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={startDateForInProgress}
                      onChange={(e) => setStartDateForInProgress(e.target.value)}
                    />
                    <small className="text-muted">Se registrará como fecha de inicio de la orden.</small>
                  </div>
                )}
                {pendingStatus.value === 'FINALIZED' && (
                  <div className="mb-3">
                    <label className="form-label">Fecha fin</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={endDateForFinalize}
                      onChange={(e) => setEndDateForFinalize(e.target.value)}
                    />
                    <small className="text-muted">Se registrará como fecha de cierre de la orden.</small>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowStatusConfirmModal(false);
                    setPendingStatus(null);
                    setStartDateForInProgress('');
                    setEndDateForFinalize('');
                  }}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  disabled={
                    (pendingStatus.value === 'IN_PROGRESS' && !startDateForInProgress.trim()) ||
                    (pendingStatus.value === 'FINALIZED' && !endDateForFinalize.trim())
                  }
                  onClick={() => {
                    const newOrder: SelectionOrder = {
                      ...order,
                      status: pendingStatus.value as any,
                      ...(pendingStatus.value === 'IN_PROGRESS' && startDateForInProgress
                        ? {
                            start_date:
                              startDateForInProgress.length === 16
                                ? `${startDateForInProgress}:00`
                                : startDateForInProgress,
                          }
                        : {}),
                      ...(pendingStatus.value === 'FINALIZED' && endDateForFinalize
                        ? {
                            end_date:
                              endDateForFinalize.length === 16
                                ? `${endDateForFinalize}:00`
                                : endDateForFinalize,
                          }
                        : {}),
                    };
                    // Enviamos el cambio al padre; el estado visual se refresca desde el backend.
                    onSave(newOrder);
                    setShowStatusConfirmModal(false);
                    setPendingStatus(null);
                    setStartDateForInProgress('');
                    setEndDateForFinalize('');
                  }}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
        </>
      )}
    </>
  );
};

export default SelectionOrderHeaderForm;